// The company admin console (design §5): overview and plan usage, people, single sign-on, connectors, branding and
// settings, every envelope's metadata, and the audit log. Data: GET /api/admin.
import { useMemo, useState } from "react";
import { Link, NavLink, Route, Routes } from "react-router-dom";
import { Building2, CheckCircle2, Circle, FileSignature, ImageUp, KeyRound, Mail, Palette, Plug, ScrollText, Search, Trash2, UserPlus, Users } from "lucide-react";
import { useMe, useReloadMe } from "../App";
import { api, useApi } from "../lib/api";
import { fullDate, ROLE_LABEL, shortDate, STATUS, when } from "../lib/format";
import type { AdminOverview, Envelope, EnvelopeStatus, Member, Role } from "../lib/types";
import { Card, Chip, StatusChip, inputCls } from "../components/bits";
import { Button, ConfirmDialog, cx, Empty, ErrorNote, Spinner, toast } from "../components/ui";
import { CompanyMark } from "../components/icons";
import CompanySso from "./CompanySso";
import CompanyConnectors from "./CompanyConnectors";

const TABS = [
  { to: "/company", label: "Overview", icon: Building2, end: true },
  { to: "/company/people", label: "People", icon: Users },
  { to: "/company/sso", label: "Single sign-on", icon: KeyRound },
  { to: "/company/connectors", label: "Connectors", icon: Plug },
  { to: "/company/branding", label: "Branding & settings", icon: Palette },
  { to: "/company/envelopes", label: "All envelopes", icon: FileSignature },
  { to: "/company/audit", label: "Audit log", icon: ScrollText },
];

export default function CompanyAdmin() {
  const { data, error, loading, reload } = useApi<AdminOverview>("/admin");
  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold">{data?.company.name ?? "Company"}</h1>
        {data && <Chip tone={data.company.status === "active" ? "good" : data.company.status === "pending" ? "warn" : "bad"}>{data.company.status === "active" ? "Active" : data.company.status === "pending" ? "Waiting for approval" : "Paused"}</Chip>}
        {data && <Chip tone="accent">{data.plan.name} plan</Chip>}
      </div>
      <nav className="flex gap-1 overflow-x-auto -mx-1 px-1 mb-5 [scrollbar-width:none] border-b border-line" aria-label="Company">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => cx("inline-flex items-center gap-1.5 px-3 h-10 text-sm font-medium whitespace-nowrap border-b-2 -mb-px", isActive ? "border-accent text-accent-text" : "border-transparent text-ink-2 hover:text-ink")}>
            <t.icon className="size-4" /> {t.label}
          </NavLink>
        ))}
      </nav>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : data ? (
        <Routes>
          <Route index element={<Overview o={data} />} />
          <Route path="people" element={<People o={data} reload={reload} />} />
          <Route path="sso" element={<CompanySso o={data} reload={reload} />} />
          <Route path="connectors" element={<CompanyConnectors o={data} reload={reload} />} />
          <Route path="branding" element={<Branding o={data} reload={reload} />} />
          <Route path="envelopes" element={<AllEnvelopes />} />
          <Route path="audit" element={<Audit />} />
        </Routes>
      ) : null}
    </div>
  );
}

function Meter({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="bg-surface border border-line rounded-xl px-4 py-3">
      <div className="text-xs text-muted">{label}</div>
      <div className="text-2xl font-semibold tabular">
        {used}
        <span className="text-sm text-muted font-normal"> / {limit ?? "unlimited"}</span>
      </div>
      {limit !== null && (
        <div className="h-1.5 rounded-full bg-surface-2 mt-2 overflow-hidden" aria-hidden>
          <div className={cx("h-full rounded-full", pct >= 90 ? "bg-bad" : pct >= 70 ? "bg-warn" : "bg-accent")} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

function Overview({ o }: { o: AdminOverview }) {
  const steps = [
    { done: o.company.status === "active", text: o.company.status === "active" ? "Approved by eSignIt" : "Waiting for eSignIt to approve your company (you'll get an email)" },
    { done: o.members.length > 1, text: "Invite your colleagues", to: "/company/people" },
    { done: o.domains.some((d) => d.verifiedAt), text: "Verify your email domain", to: "/company/sso" },
    { done: o.idps.some((i) => i.testedAt), text: o.plan.sso ? "Connect your Entra ID (single sign-on)" : "Single sign-on (Team plan and up)", to: "/company/sso" },
    { done: o.connectors.some((c) => c.status === "connected"), text: "Connect your CRM or storage", to: "/company/connectors" },
    { done: Boolean(o.company.branding.logoPng), text: "Add your logo for signer emails and pages", to: "/company/branding" },
  ];
  const active = o.members.filter((m) => m.status !== "disabled").length;
  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_22rem] gap-5">
      <div className="space-y-5 min-w-0">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Meter label="Envelopes sent this month" used={o.usage.envelopes} limit={o.plan.envelopesPerMonth} />
          <Meter label="People" used={active} limit={o.plan.users} />
          <div className="bg-surface border border-line rounded-xl px-4 py-3">
            <div className="text-xs text-muted">Emails this month</div>
            <div className="text-2xl font-semibold tabular">{o.usage.sent}</div>
            <div className="text-xs text-muted mt-1">
              {o.usage.bounced} bounced · {o.usage.complained} spam reports
            </div>
          </div>
        </div>
        <Card title="Getting set up">
          <ul className="space-y-2">
            {steps.map((s) => (
              <li key={s.text} className="flex items-center gap-2 text-sm">
                {s.done ? <CheckCircle2 className="size-4 text-good shrink-0" /> : <Circle className="size-4 text-muted shrink-0" />}
                {s.to && !s.done ? (
                  <Link to={s.to} className="text-accent-text hover:underline">
                    {s.text}
                  </Link>
                ) : (
                  <span className={s.done ? "text-ink-2" : "text-ink"}>{s.text}</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <Card title="Your plan">
        <div className="text-sm space-y-1.5">
          <div className="text-lg font-semibold">{o.plan.name}</div>
          <div>{o.plan.envelopesPerMonth.toLocaleString()} envelopes a month</div>
          <div>{o.plan.users === null ? "Unlimited people" : `${o.plan.users} ${o.plan.users === 1 ? "person" : "people"}`}</div>
          <div>{o.plan.sso ? "Single sign-on included" : "No single sign-on"}</div>
          <div>{o.plan.connectors === "all" ? "All connectors" : "Webhooks only"}</div>
          <p className="text-xs text-muted pt-2">To change plans, contact eSignIt. Online billing is coming.</p>
        </div>
      </Card>
    </div>
  );
}

function People({ o, reload }: { o: AdminOverview; reload: () => Promise<void> }) {
  const me = useMe();
  const [invite, setInvite] = useState({ email: "", name: "", role: "sender" as Role });
  const [busy, setBusy] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast(ok);
      await reload();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="space-y-5">
      <Card title="Invite someone">
        <form
          className="grid sm:grid-cols-[1fr_1fr_10rem_auto] gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void run("invite", () => api("/admin/members", { body: invite }), `Invitation sent to ${invite.email}`).then(() => setInvite({ email: "", name: "", role: "sender" }));
          }}
        >
          <input required type="email" placeholder="Email" aria-label="Email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className={inputCls} />
          <input placeholder="Name (optional)" aria-label="Name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className={inputCls} />
          <select aria-label="Role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value as Role })} className={inputCls}>
            <option value="sender">Sender</option>
            <option value="admin">Admin</option>
            {me.role === "owner" && <option value="owner">Owner</option>}
          </select>
          <Button variant="primary" type="submit" busy={busy === "invite"}>
            <UserPlus className="size-4" /> Invite
          </Button>
        </form>
        <p className="text-xs text-muted mt-2">Senders send their own envelopes. Admins also manage people, single sign-on, connectors and settings, and see every envelope's status. Owners can also manage other owners.</p>
      </Card>
      <div className="bg-surface border border-line rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted border-b border-line">
            <tr>
              <th className="px-4 py-2 font-medium">Person</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Last seen</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {o.members.map((m) => {
              const self = m.email === me.email;
              return (
                <tr key={m.email}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-ink">{m.name || m.email}</div>
                    {m.name && <div className="text-xs text-muted">{m.email}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <select
                      aria-label={`Role for ${m.email}`}
                      value={m.role}
                      disabled={self || busy !== null || (m.role === "owner" && me.role !== "owner")}
                      onChange={(e) => run(`role:${m.email}`, () => api("/admin/members/update", { body: { email: m.email, role: e.target.value } }), "Role changed")}
                      className={cx(inputCls, "h-8")} style={{ width: "7.5rem" }}
                    >
                      {(["sender", "admin", "owner"] as Role[]).filter((r) => r !== "owner" || me.role === "owner" || m.role === "owner").map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2.5">
                    {m.status === "active" ? <Chip tone="good">Active</Chip> : m.status === "invited" ? <Chip tone="accent">Invited {when(m.invitedAt)}</Chip> : <Chip tone="bad">Turned off</Chip>}
                  </td>
                  <td className="px-4 py-2.5 text-muted whitespace-nowrap">{m.lastSeenAt ? when(m.lastSeenAt) : "—"}</td>
                  <td className="px-4 py-2.5">
                    {!self && (
                      <div className="flex justify-end gap-1.5">
                        {m.status === "invited" && (
                          <Button className="h-8" busy={busy === `resend:${m.email}`} onClick={() => run(`resend:${m.email}`, () => api("/admin/members/resend", { body: { email: m.email } }), "Invitation sent again")}>
                            <Mail className="size-3.5" /> Resend
                          </Button>
                        )}
                        <Button className="h-8" busy={busy === `status:${m.email}`} onClick={() => run(`status:${m.email}`, () => api("/admin/members/update", { body: { email: m.email, status: m.status === "disabled" ? "active" : "disabled" } }))}>
                          {m.status === "disabled" ? "Turn on" : "Turn off"}
                        </Button>
                        <button className="p-2 rounded-lg text-muted hover:bg-surface-2 hover:text-bad" aria-label={`Remove ${m.email}`} title="Remove" onClick={() => setRemoving(m)}>
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.name || removing.email}?`}
          confirm="Remove"
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await api(`/admin/members?${new URLSearchParams({ email: removing.email })}`, { method: "DELETE" });
            toast("Removed");
            await reload();
          }}
        >
          <p>They lose access to eSignIt for {o.company.name}. Envelopes they sent stay, with their audit trails.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/** Scales a picked image to fit 600×200 and returns a PNG data URL (keeps the upload under 100 KB). */
async function logoPng(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error("That file isn't an image"));
      i.src = url;
    });
    const s = Math.min(1, 600 / img.width, 200 / img.height);
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(img.width * s));
    c.height = Math.max(1, Math.round(img.height * s));
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

function Branding({ o, reload }: { o: AdminOverview; reload: () => Promise<void> }) {
  const reloadMe = useReloadMe();
  const c = o.company;
  const [name, setName] = useState(c.name);
  const [displayName, setDisplayName] = useState(c.branding.displayName);
  const [color, setColor] = useState(c.branding.color);
  const [logo, setLogo] = useState<string | null | undefined>(undefined); // undefined = unchanged, null = remove
  const [s, setS] = useState(c.settings);
  const [busy, setBusy] = useState(false);
  const shownLogo = logo === undefined ? c.branding.logoPng : logo ?? undefined;
  const save = async () => {
    setBusy(true);
    try {
      await api("/admin/company", { body: { name, branding: { displayName, color, ...(logo !== undefined ? { logo } : {}) }, settings: s } });
      toast("Saved");
      setLogo(undefined);
      await Promise.all([reload(), reloadMe()]);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <Card title="Branding">
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="block font-medium text-ink-2 mb-1">Company name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </label>
          <label className="block text-sm">
            <span className="block font-medium text-ink-2 mb-1">Name signers see</span>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputCls} />
            <span className="block text-xs text-muted mt-1">“Sent by {displayName || name} via eSignIt”</span>
          </label>
          <label className="block text-sm">
            <span className="block font-medium text-ink-2 mb-1">Brand colour</span>
            <span className="flex gap-2">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} aria-label="Brand colour" className="h-9 w-12 rounded border border-line bg-surface" />
              <input value={color} onChange={(e) => setColor(e.target.value)} className={cx(inputCls, "font-mono")} style={{ width: "8rem" }} aria-label="Brand colour hex" />
            </span>
          </label>
          <div className="text-sm">
            <span className="block font-medium text-ink-2 mb-1">Logo</span>
            <div className="flex items-center gap-3">
              <div className="h-14 min-w-28 rounded-lg border border-line bg-surface-2 flex items-center justify-center px-3">{shownLogo ? <CompanyMark name={name} logo={shownLogo} height={36} /> : <span className="text-xs text-muted">No logo</span>}</div>
              <label className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 h-9 text-sm font-medium cursor-pointer hover:bg-surface-2">
                <ImageUp className="size-4" /> Upload
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  hidden
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) setLogo(await logoPng(f).catch((x) => (toast((x as Error).message, "error"), undefined)));
                  }}
                />
              </label>
              {shownLogo && (
                <Button variant="ghost" onClick={() => setLogo(null)}>
                  Remove
                </Button>
              )}
            </div>
          </div>
          {/* What a signer's email header looks like */}
          <div className="rounded-xl border border-line overflow-hidden mt-2" aria-label="Email preview">
            <div className="h-1" style={{ background: color }} />
            <div className="p-4">
              {shownLogo ? <CompanyMark name={displayName || name} logo={shownLogo} height={30} /> : <span className="font-bold">{displayName || name}</span>}
              <div className="mt-3 text-sm font-semibold">Please review and sign</div>
              <div className="mt-3 inline-block rounded-lg px-4 py-2 text-sm font-semibold text-white" style={{ background: color }}>
                Review and sign
              </div>
            </div>
          </div>
        </div>
      </Card>
      <Card title="Defaults and retention">
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="block font-medium text-ink-2 mb-1">New envelopes expire after (days)</span>
            <input type="number" min={1} max={120} value={s.defaultExpiryDays} onChange={(e) => setS({ ...s, defaultExpiryDays: Number(e.target.value) })} className={inputCls} />
          </label>
          <label className="block">
            <span className="block font-medium text-ink-2 mb-1">Remind signers every (days, 0 = off)</span>
            <input type="number" min={0} max={30} value={s.defaultReminderDays} onChange={(e) => setS({ ...s, defaultReminderDays: Number(e.target.value) })} className={inputCls} />
          </label>
          <label className="block">
            <span className="block font-medium text-ink-2 mb-1">Keep completed documents locked for (years)</span>
            <input type="number" min={1} max={10} value={s.retentionYears} onChange={(e) => setS({ ...s, retentionYears: Number(e.target.value) })} className={inputCls} />
            <span className="block text-xs text-muted mt-1">Applies to envelopes completed from now on. Locked documents can't be deleted or changed by anyone until then.</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-1" checked={s.adminsCanDownload} onChange={(e) => setS({ ...s, adminsCanDownload: e.target.checked })} />
            <span>
              <span className="font-medium text-ink">Admins can open everyone's documents</span>
              <span className="block text-xs text-muted">Off: admins see status and audit trails only.</span>
            </span>
          </label>
        </div>
      </Card>
      <div className="lg:col-span-2 flex justify-end">
        <Button variant="primary" busy={busy} onClick={save}>
          Save changes
        </Button>
      </div>
    </div>
  );
}

function AllEnvelopes() {
  const { data, error, loading } = useApi<{ envelopes: Envelope[] }>("/admin/envelopes");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<EnvelopeStatus | "">("");
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (data?.envelopes ?? []).filter((e) => (!status || e.status === status) && (!n || e.title.toLowerCase().includes(n) || e.ownerEmail.includes(n) || e.ownerName.toLowerCase().includes(n)));
  }, [data, q, status]);
  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative sm:w-72">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title or sender" aria-label="Search" className={cx(inputCls, "pl-9")} />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value as EnvelopeStatus | "")} className={cx(inputCls, "sm:w-52")} aria-label="Status">
          <option value="">All statuses</option>
          {Object.entries(STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : !list.length ? (
        <Empty icon={<FileSignature className="size-10" />} title="No envelopes" />
      ) : (
        <div className="bg-surface border border-line rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">Title</th>
                <th className="px-4 py-2 font-medium">Sender</th>
                <th className="px-4 py-2 font-medium">Signed</th>
                <th className="px-4 py-2 font-medium">Created</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.map((e) => (
                <tr key={e.id} className="hover:bg-surface-2">
                  <td className="px-4 py-2.5 max-w-xs">
                    <Link to={`/company/envelopes/${e.id}`} className="font-medium text-ink hover:underline line-clamp-1">
                      {e.title}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-ink-2 whitespace-nowrap">{e.ownerName}</td>
                  <td className="px-4 py-2.5 tabular text-ink-2">{e.status === "draft" ? "—" : `${e.signedCount}/${e.signerCount}`}</td>
                  <td className="px-4 py-2.5 text-muted whitespace-nowrap">{shortDate(e.createdAt)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <StatusChip status={e.status} />
                      {e.deliveryState === "failed" && <Chip tone="bad">Connector failed</Chip>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

const AUDIT_LABEL: Record<string, string> = {
  company_created: "Company registered",
  company_updated: "Company settings changed",
  member_invited: "Person invited",
  member_updated: "Person changed",
  member_removed: "Person removed",
  domain_added: "Domain added",
  domain_verified: "Domain verified",
  domain_removed: "Domain removed",
  sso_added: "Single sign-on provider added",
  sso_replaced: "Single sign-on provider replaced",
  sso_removed: "Single sign-on provider removed",
  sso_tested: "Single sign-on tested",
  sso_settings: "Single sign-on settings changed",
  connector_added: "Connector added",
  connector_updated: "Connector changed",
  connector_connected: "Connector connected",
  connector_removed: "Connector removed",
  platform_update: "Changed by eSignIt",
  auto_suspended: "Sending paused automatically",
};

function Audit() {
  const { data, error, loading } = useApi<{ entries: { ts: string; actor: string; action: string; detail?: Record<string, unknown> }[] }>("/admin/audit");
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Spinner />;
  return (
    <div className="bg-surface border border-line rounded-xl divide-y divide-line">
      {!data?.entries.length && <Empty icon={<ScrollText className="size-10" />} title="Nothing yet" />}
      {data?.entries.map((e, i) => (
        <div key={i} className="px-4 py-2.5 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
          <span className="text-muted whitespace-nowrap sm:w-44">{fullDate(e.ts)}</span>
          <span className="font-medium sm:w-64">{AUDIT_LABEL[e.action] ?? e.action}</span>
          <span className="text-ink-2 truncate">{e.actor}</span>
          {e.detail && <span className="text-xs text-muted truncate">{Object.entries(e.detail).map(([k, v]) => `${k}: ${v}`).join(" · ")}</span>}
        </div>
      ))}
    </div>
  );
}
